import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import {
  useGetMembersQuery,
  useUpdateMemberMutation,
  useRejectSelfRegistrationMutation,
} from "../../../features/members/membersApiSlice";
import { displayDate } from "../../../utils/dataUtils";
import { setDialog } from "../../../features/dialog/dialogSlice";
import MemberDetails from "./MemberDetails";

const SelfRegistrationsView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: members, isLoading } = useGetMembersQuery();
  const [updateMember] = useUpdateMemberMutation();
  const [rejectSelfRegistration] = useRejectSelfRegistrationMutation();
  const [expandedId, setExpandedId] = useState(null);
  const [actionState, setActionState] = useState({ id: null, action: null });

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [memberToReject, setMemberToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const pendingMembers = useMemo(() => {
    const list = Array.isArray(members) ? members : [];
    return list
      .filter((member) => (member?.status || "").toLowerCase() === "pending")
      .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
  }, [members]);

  const handleToggleDetails = (memberId) => {
    setExpandedId((prev) => (prev === memberId ? null : memberId));
  };

  const handleApprove = async (member) => {
    if (!member?._id) return;
    setActionState({ id: member._id, action: "active" });
    try {
      await updateMember({ id: member._id, status: "active" }).unwrap();
      dispatch(
        setDialog({
          title: "Anmeldung bestätigt",
          type: "SUCCESS",
          data: {
            successText: "Die Anmeldung wurde bestätigt und das Mitglied wurde informiert.",
          },
        })
      );
    } catch (error) {
      console.error("Approve member error:", error);
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText: "Die Anmeldung konnte nicht bestätigt werden. Bitte erneut versuchen.",
          },
        })
      );
    } finally {
      setActionState({ id: null, action: null });
    }
  };

  const openRejectDialog = (member) => {
    setMemberToReject(member);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!memberToReject?._id) return;
    setRejectDialogOpen(false);
    setActionState({ id: memberToReject._id, action: "rejected" });

    try {
      // contractRef may be populated (object) or just an ID
      const contractId =
        typeof memberToReject?.contractRef === "object"
          ? memberToReject.contractRef?._id
          : memberToReject?.contractRef;

      if (contractId) {
        // Self-registered member: send rejection email with optional reason
        await rejectSelfRegistration({
          contractId,
          reason: rejectionReason.trim(),
        }).unwrap();
      } else {
        // Manually created pending member: just update status
        await updateMember({ id: memberToReject._id, status: "rejected" }).unwrap();
      }

      dispatch(
        setDialog({
          title: "Anmeldung abgelehnt",
          type: "SUCCESS",
          data: {
            successText: rejectionReason.trim()
              ? "Die Anmeldung wurde abgelehnt und das Mitglied wurde mit Begründung informiert."
              : "Die Anmeldung wurde abgelehnt und das Mitglied wurde informiert.",
          },
        })
      );
    } catch (error) {
      console.error("Reject member error:", error);
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message ||
              "Die Anmeldung konnte nicht abgelehnt werden. Bitte erneut versuchen.",
          },
        })
      );
    } finally {
      setActionState({ id: null, action: null });
      setMemberToReject(null);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const rejectMemberName = memberToReject
    ? `${memberToReject.firstName || ""} ${memberToReject.lastName || ""}`.trim()
    : "";

  return (
    <Box>
      <Button
        onClick={() => navigate(-1)}
        variant="outlined"
        color="inherit"
        sx={{ mb: 3 }}
        startIcon={<Icon width="26" icon="fa-solid:arrow-left" />}
      >
        <Typography display="inline" variant="h4" sx={{ mb: 0 }}>
          Zurück
        </Typography>
      </Button>

      <Stack spacing={2}>
        <Typography variant="h4">Selbstanmeldungen</Typography>

        {pendingMembers.length === 0 ? (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="body1">
              Keine offenen Selbstanmeldungen vorhanden.
            </Typography>
          </Paper>
        ) : (
          pendingMembers.map((member) => {
            const isExpanded = expandedId === member._id;
            const isBusy = actionState.id === member._id;
            const fullName = `${member?.firstName || ""} ${member?.lastName || ""}`.trim();
            const siteName = member?.standortRef?.siteName || "—";
            const createdAt = displayDate(member?.createdAt);

            return (
              <Paper key={member._id} elevation={3} sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Vollständiger Name
                    </Typography>
                    <Typography variant="h6">{fullName || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Standort
                    </Typography>
                    <Typography variant="h6">{siteName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Registriert am
                    </Typography>
                    <Typography variant="h6">{createdAt}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      onClick={() => handleToggleDetails(member._id)}
                      endIcon={
                        <Icon
                          width="18"
                          icon={
                            isExpanded
                              ? "mdi:chevron-up"
                              : "mdi:chevron-down"
                          }
                        />
                      }
                    >
                      {isExpanded ? "Details ausblenden" : "Details anzeigen"}
                    </Button>
                  </Grid>
                </Grid>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider sx={{ my: 2 }} />
                  <MemberDetails member={member} />
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={isBusy}
                      onClick={() => handleApprove(member)}
                    >
                      {isBusy && actionState.action === "active"
                        ? "Wird bestätigt…"
                        : "Annehmen"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={isBusy}
                      onClick={() => openRejectDialog(member)}
                    >
                      {isBusy && actionState.action === "rejected"
                        ? "Wird abgelehnt…"
                        : "Ablehnen"}
                    </Button>
                  </Stack>
                </Collapse>
              </Paper>
            );
          })
        )}
      </Stack>

      {/* Rejection dialog with optional reason */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Anmeldung ablehnen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Möchten Sie die Anmeldung von{" "}
            <strong>{rejectMemberName}</strong> ablehnen?
            <br />
            Eine Ablehnungs-E-Mail wird automatisch versendet.
          </DialogContentText>
          <TextField
            label="Begründung (optional)"
            helperText="Die Begründung wird in der Ablehnungs-E-Mail an das Mitglied übermittelt."
            multiline
            rows={3}
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="z.B. Kapazitätsgrenze erreicht, fehlende Unterlagen, …"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
          >
            Ablehnen bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelfRegistrationsView;
